import { api } from "../http";
import type {
  CountryDocumentationRequirement,
  Page,
  UpsertCountryDocumentationRequirement,
} from "../types";

const BASE_PATH = "/api/admin/country-documentation";

const toStringArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((doc) => String(doc).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((doc) => doc.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeRequirement = (input: any): CountryDocumentationRequirement => ({
  id: String(input?.id ?? input?.uuid ?? ""),
  country: input?.country ?? input?.nation ?? "",
  documents: (() => {
    const docs = toStringArray(input?.documents);
    if (docs.length) return docs;
    return toStringArray(input?.requiredDocuments);
  })(),
  notes: input?.notes ?? input?.remarks ?? null,
  updatedAt: input?.updatedAt ?? input?.modifiedAt ?? input?.createdAt ?? null,
  updatedBy: input?.updatedBy ?? input?.modifiedBy ?? input?.createdBy ?? null,
});

const extractRequirements = (payload: any): CountryDocumentationRequirement[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    return payload.map(normalizeRequirement);
  }
  const page = payload as Page<any>;
  if (Array.isArray(page?.content)) {
    return page.content.map(normalizeRequirement);
  }
  if (Array.isArray((payload as any)?.data?.content)) {
    return (payload as any).data.content.map(normalizeRequirement);
  }
  if (Array.isArray((payload as any)?.items)) {
    return (payload as any).items.map(normalizeRequirement);
  }
  return [];
};

export const countryDocumentationApi = {
  async list(page = 0, size = 200): Promise<CountryDocumentationRequirement[]> {
    const response = await api.get<
      Page<CountryDocumentationRequirement> | CountryDocumentationRequirement[] | any
    >(`${BASE_PATH}?page=${page}&size=${size}`);
    return extractRequirements(response);
  },

  async get(id: string): Promise<CountryDocumentationRequirement> {
    const response = await api.get<CountryDocumentationRequirement | any>(`${BASE_PATH}/${id}`);
    return normalizeRequirement(response);
  },

  create(payload: UpsertCountryDocumentationRequirement) {
    return api.post<CountryDocumentationRequirement, UpsertCountryDocumentationRequirement>(BASE_PATH, payload);
  },

  update(id: string, payload: UpsertCountryDocumentationRequirement) {
    return api.put<CountryDocumentationRequirement, UpsertCountryDocumentationRequirement>(`${BASE_PATH}/${id}`, payload);
  },

  remove(id: string) {
    return api.delete<void>(`${BASE_PATH}/${id}`);
  },
};
